import type {
  DatasetManifest,
  MoneyValue,
  NormalizedNotice,
  NoticeAttachment,
  NoticeChange,
  NoticeEvaluation,
  SyncRun,
} from "../notices/types";
import { reapplyStoredCompanyProfile } from "./reevaluate";
import { listDashboardNotices, toRemoteNoticeRecord } from "./repository";
import type { BrowserDatabase, RemoteNoticeRecord } from "./schema";

export const MIN_SUPPORTED_DATASET_SCHEMA_VERSION = 1;
export const MAX_SUPPORTED_DATASET_SCHEMA_VERSION = 1;
export const DATASET_HASH_DELIMITER = "\n---\n";

export interface DatasetTextFiles {
  noticesText: string;
  changesText: string;
  evaluationsText: string;
}

export interface ValidatedDataset {
  manifest: DatasetManifest;
  notices: NormalizedNotice[];
  changes: NoticeChange[];
  evaluations: NoticeEvaluation[];
}

export interface DatasetSyncOptions {
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
  now?: () => Date;
  createRunId?: () => string;
  minSchemaVersion?: number;
  maxSchemaVersion?: number;
  crypto?: Crypto;
}

export interface DatasetSyncResult {
  status: "success" | "skipped";
  manifest: DatasetManifest;
  run: SyncRun;
}

export class DatasetValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DatasetValidationError";
  }
}

export class DatasetSchemaVersionError extends DatasetValidationError {
  readonly receivedVersion: number;
  readonly minimumVersion: number;
  readonly maximumVersion: number;

  constructor(receivedVersion: number, minimumVersion: number, maximumVersion: number) {
    super(
      `Dataset schema version ${receivedVersion} is unsupported; supported range is ${minimumVersion}-${maximumVersion}.`,
    );
    this.name = "DatasetSchemaVersionError";
    this.receivedVersion = receivedVersion;
    this.minimumVersion = minimumVersion;
    this.maximumVersion = maximumVersion;
  }
}

export class DatasetHashMismatchError extends DatasetValidationError {
  constructor() {
    super("Dataset content hash does not match the manifest.");
    this.name = "DatasetHashMismatchError";
  }
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function assert(condition: unknown, message: string): asserts condition {
  if (!condition) throw new DatasetValidationError(message);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isNullableFiniteNumber(value: unknown): value is number | null {
  return value === null || (typeof value === "number" && Number.isFinite(value));
}

function isIsoDateOrNull(value: unknown): value is string | null {
  return (
    value === null ||
    (typeof value === "string" && value.length > 0 && !Number.isNaN(Date.parse(value)))
  );
}

function assertMoneyValue(value: unknown, path: string): asserts value is MoneyValue | null {
  if (value === null) return;
  assert(isRecord(value), `${path} must be an object or null.`);
  assert(typeof value.raw === "string", `${path}.raw must be a string.`);
  assert(isNullableFiniteNumber(value.amountKrw), `${path}.amountKrw is invalid.`);
  assert(["KRW", "percent", "unknown"].includes(String(value.unit)), `${path}.unit is invalid.`);
  assert(
    ["maximum", "minimum", "fixed", "ratio", "unknown"].includes(String(value.kind)),
    `${path}.kind is invalid.`,
  );
}

function assertAttachment(value: unknown, path: string): asserts value is NoticeAttachment {
  assert(isRecord(value), `${path} must be an object.`);
  for (const field of ["noticeId", "fileName", "sourceUrl", "extension", "lastCheckedAt"] as const) {
    assert(typeof value[field] === "string", `${path}.${field} must be a string.`);
  }
  assert(isNullableFiniteNumber(value.sizeBytes), `${path}.sizeBytes is invalid.`);
  assert(isNullableString(value.sha256), `${path}.sha256 is invalid.`);
  assert(isNullableString(value.extractedTextHash), `${path}.extractedTextHash is invalid.`);
  assert(
    ["pending", "success", "unsupported", "failed"].includes(String(value.extractionStatus)),
    `${path}.extractionStatus is invalid.`,
  );
}

function assertNotice(value: unknown, index: number): asserts value is NormalizedNotice {
  const path = `notices[${index}]`;
  assert(isRecord(value), `${path} must be an object.`);
  const expectedFields = [
    "id",
    "canonicalKey",
    "title",
    "agency",
    "supervisingMinistry",
    "announcementNo",
    "category",
    "publishedAt",
    "applicationStartAt",
    "applicationEndAt",
    "originalUrl",
    "sourceNames",
    "sourceNoticeIds",
    "summary",
    "eligibilityText",
    "supportAmount",
    "selfContribution",
    "requiredDocuments",
    "evaluationCriteria",
    "attachments",
    "contentHash",
    "firstSeenAt",
    "lastSeenAt",
    "status",
  ] as const;
  const actualFields = Object.keys(value).sort();
  const contractFields = [...expectedFields].sort();
  assert(
    actualFields.length === contractFields.length &&
      actualFields.every((field, fieldIndex) => field === contractFields[fieldIndex]),
    `${path} must contain exactly the 24-field notice contract.`,
  );
  for (const field of [
    "id",
    "canonicalKey",
    "title",
    "agency",
    "originalUrl",
    "contentHash",
  ] as const) {
    assert(typeof value[field] === "string" && value[field].length > 0, `${path}.${field} is required.`);
  }
  for (const field of ["supervisingMinistry", "summary", "eligibilityText"] as const) {
    assert(typeof value[field] === "string", `${path}.${field} must be a string.`);
  }
  assert(isNullableString(value.announcementNo), `${path}.announcementNo is invalid.`);
  assert(
    ["AI/R&D", "사업화", "지역", "조달", "기타"].includes(String(value.category)),
    `${path}.category is invalid.`,
  );
  for (const field of [
    "publishedAt",
    "applicationStartAt",
    "applicationEndAt",
  ] as const) {
    assert(isIsoDateOrNull(value[field]), `${path}.${field} is invalid.`);
  }
  for (const field of ["firstSeenAt", "lastSeenAt"] as const) {
    assert(isIsoDateOrNull(value[field]) && value[field] !== null, `${path}.${field} is invalid.`);
  }
  for (const field of [
    "sourceNames",
    "sourceNoticeIds",
    "requiredDocuments",
    "evaluationCriteria",
  ] as const) {
    assert(isStringArray(value[field]), `${path}.${field} must be a string array.`);
  }
  assertMoneyValue(value.supportAmount, `${path}.supportAmount`);
  assertMoneyValue(value.selfContribution, `${path}.selfContribution`);
  assert(Array.isArray(value.attachments), `${path}.attachments must be an array.`);
  value.attachments.forEach((attachment, attachmentIndex) =>
    assertAttachment(attachment, `${path}.attachments[${attachmentIndex}]`),
  );
  assert(
    ["접수예정", "접수중", "마감", "확인필요"].includes(String(value.status)),
    `${path}.status is invalid.`,
  );
}

function assertChange(value: unknown, index: number): asserts value is NoticeChange {
  const path = `changes[${index}]`;
  assert(isRecord(value), `${path} must be an object.`);
  for (const field of ["id", "noticeId", "fieldName", "detectedAt", "toContentHash"] as const) {
    assert(typeof value[field] === "string" && value[field].length > 0, `${path}.${field} is required.`);
  }
  assert(
    ["new", "updated", "deadline", "attachment"].includes(String(value.changeType)),
    `${path}.changeType is invalid.`,
  );
  assert(
    ["매우 높음", "높음", "중간", "낮음"].includes(String(value.importance)),
    `${path}.importance is invalid.`,
  );
  assert(isNullableString(value.fromContentHash), `${path}.fromContentHash is invalid.`);
}

function assertEvaluation(value: unknown, index: number): asserts value is NoticeEvaluation {
  const path = `evaluations[${index}]`;
  assert(isRecord(value), `${path} must be an object.`);
  for (const field of [
    "noticeId",
    "companyProfileVersion",
    "noticeContentHash",
    "recommendedAction",
    "evaluatedAt",
  ] as const) {
    assert(typeof value[field] === "string" && value[field].length > 0, `${path}.${field} is required.`);
  }
  for (const field of [
    "eligibilityReasons",
    "fitReasons",
    "risks",
    "requiredConfirmations",
  ] as const) {
    assert(isStringArray(value[field]), `${path}.${field} must be a string array.`);
  }
  assert(
    ["충족", "조건부 충족", "미확인", "불충족"].includes(String(value.eligibilityStatus)),
    `${path}.eligibilityStatus is invalid.`,
  );
  assert(
    typeof value.fitScore === "number" &&
      Number.isFinite(value.fitScore) &&
      value.fitScore >= 0 &&
      value.fitScore <= 100,
    `${path}.fitScore is invalid.`,
  );
  assert(
    ["매우 적합", "적합", "조건부 적합", "부적합 가능성 높음", "사실상 부적합"].includes(
      String(value.fitGrade),
    ),
    `${path}.fitGrade is invalid.`,
  );
  assert(["rule", "ai"].includes(String(value.evaluationMethod)), `${path}.evaluationMethod is invalid.`);
  assert(isNullableString(value.modelName), `${path}.modelName is invalid.`);
  assert(isNullableString(value.promptVersion), `${path}.promptVersion is invalid.`);
}

function assertManifest(value: unknown): asserts value is DatasetManifest {
  assert(isRecord(value), "manifest must be an object.");
  assert(Number.isInteger(value.schemaVersion), "manifest.schemaVersion must be an integer.");
  for (const field of ["datasetVersion", "contentHash", "generatedAt"] as const) {
    assert(typeof value[field] === "string" && value[field].length > 0, `manifest.${field} is required.`);
  }
  for (const field of ["noticeCount", "changeCount", "evaluationCount"] as const) {
    assert(
      Number.isInteger(value[field]) && Number(value[field]) >= 0,
      `manifest.${field} must be a non-negative integer.`,
    );
  }
  assert(isRecord(value.sources), "manifest.sources must be an object.");
  for (const [sourceName, source] of Object.entries(value.sources)) {
    assert(isRecord(source), `manifest.sources.${sourceName} must be an object.`);
    assert(
      ["success", "partial", "failed", "demo"].includes(String(source.status)),
      `manifest.sources.${sourceName}.status is invalid.`,
    );
    assert(
      isIsoDateOrNull(source.lastSuccessAt),
      `manifest.sources.${sourceName}.lastSuccessAt is invalid.`,
    );
    assert(
      source.message === undefined || typeof source.message === "string",
      `manifest.sources.${sourceName}.message is invalid.`,
    );
  }
}

function parseJson(text: string, label: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new DatasetValidationError(`${label} is not valid JSON.`);
  }
}

function assertUnique<T>(
  values: readonly T[],
  selectKey: (value: T) => string,
  label: string,
): void {
  const seen = new Set<string>();
  for (const value of values) {
    const key = selectKey(value);
    assert(!seen.has(key), `${label} contains duplicate key ${key}.`);
    seen.add(key);
  }
}

export function parseAndValidateManifest(text: string): DatasetManifest {
  const manifest = parseJson(text, "manifest.json");
  assertManifest(manifest);
  return manifest;
}

export function parseAndValidateDatasetFiles(
  manifest: DatasetManifest,
  texts: DatasetTextFiles,
): Omit<ValidatedDataset, "manifest"> {
  const notices = parseJson(texts.noticesText, "notices.json");
  const changes = parseJson(texts.changesText, "changes.json");
  const evaluations = parseJson(texts.evaluationsText, "evaluations.json");

  assert(Array.isArray(notices), "notices.json must contain an array.");
  assert(Array.isArray(changes), "changes.json must contain an array.");
  assert(Array.isArray(evaluations), "evaluations.json must contain an array.");
  notices.forEach(assertNotice);
  changes.forEach(assertChange);
  evaluations.forEach(assertEvaluation);

  assert(notices.length === manifest.noticeCount, "manifest.noticeCount does not match notices.json.");
  assert(changes.length === manifest.changeCount, "manifest.changeCount does not match changes.json.");
  assert(
    evaluations.length === manifest.evaluationCount,
    "manifest.evaluationCount does not match evaluations.json.",
  );

  assertUnique(notices, (notice) => notice.id, "notices.json");
  assertUnique(notices, (notice) => notice.canonicalKey, "notices.json canonicalKey");
  assertUnique(changes, (change) => change.id, "changes.json");
  assertUnique(evaluations, (evaluation) => evaluation.noticeId, "evaluations.json");

  const noticeIds = new Set(notices.map((notice) => notice.id));
  for (const change of changes) {
    assert(noticeIds.has(change.noticeId), `changes.json references unknown notice ${change.noticeId}.`);
  }
  for (const evaluation of evaluations) {
    assert(
      noticeIds.has(evaluation.noticeId),
      `evaluations.json references unknown notice ${evaluation.noticeId}.`,
    );
    const notice = notices.find((candidate) => candidate.id === evaluation.noticeId);
    assert(
      notice?.contentHash === evaluation.noticeContentHash,
      `evaluations.json content hash is stale for notice ${evaluation.noticeId}.`,
    );
  }

  return { notices, changes, evaluations };
}

function defaultCrypto(): Crypto {
  if (!globalThis.crypto?.subtle) {
    throw new DatasetValidationError("Web Crypto SHA-256 is unavailable.");
  }
  return globalThis.crypto;
}

async function sha256Hex(input: string, cryptoProvider = defaultCrypto()): Promise<string> {
  const digest = await cryptoProvider.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(input),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/** Hash contract shared with the static writer; source-status is excluded. */
export async function computeDatasetHash(
  texts: DatasetTextFiles,
  cryptoProvider?: Crypto,
): Promise<string> {
  return sha256Hex(
    [texts.noticesText, texts.changesText, texts.evaluationsText].join(
      DATASET_HASH_DELIMITER,
    ),
    cryptoProvider,
  );
}

function joinDataUrl(baseUrl: string, fileName: string): string {
  return `${baseUrl.replace(/\/$/, "")}/${fileName}`;
}

async function fetchText(
  fetcher: typeof globalThis.fetch,
  url: string,
): Promise<string> {
  const response = await fetcher(url, { cache: "no-cache" });
  if (!response.ok) {
    throw new DatasetValidationError(`Unable to load ${url} (HTTP ${response.status}).`);
  }
  return response.text();
}

function defaultRunId(): string {
  return globalThis.crypto?.randomUUID?.() ??
    `sync-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function safeErrorMessage(error: unknown): string {
  if (error instanceof DatasetSchemaVersionError) return error.message;
  if (error instanceof DatasetValidationError) return error.message;
  return error instanceof Error ? error.message : "Dataset synchronization failed.";
}

async function recordFailedRun(
  database: BrowserDatabase,
  run: SyncRun,
): Promise<void> {
  await database.syncRuns.put(run);
}

export async function synchronizeDataset(
  database: BrowserDatabase,
  options: DatasetSyncOptions = {},
): Promise<DatasetSyncResult> {
  const fetcher = options.fetch ?? globalThis.fetch;
  if (!fetcher) throw new DatasetValidationError("Fetch API is unavailable.");

  const baseUrl = options.baseUrl ?? "/data";
  const now = options.now ?? (() => new Date());
  const startedAt = now().toISOString();
  const runId = (options.createRunId ?? defaultRunId)();
  let manifest: DatasetManifest | null = null;

  try {
    const manifestText = await fetchText(fetcher, joinDataUrl(baseUrl, "manifest.json"));
    manifest = parseAndValidateManifest(manifestText);
    const validatedManifest = manifest;

    const minimum = options.minSchemaVersion ?? MIN_SUPPORTED_DATASET_SCHEMA_VERSION;
    const maximum = options.maxSchemaVersion ?? MAX_SUPPORTED_DATASET_SCHEMA_VERSION;
    if (validatedManifest.schemaVersion < minimum || validatedManifest.schemaVersion > maximum) {
      throw new DatasetSchemaVersionError(validatedManifest.schemaVersion, minimum, maximum);
    }

    const localHash = (await database.settings.get("lastSyncContentHash"))?.value;
    if (localHash === validatedManifest.contentHash) {
      const finishedAt = now().toISOString();
      const run: SyncRun = {
        id: runId,
        datasetVersion: validatedManifest.datasetVersion,
        contentHash: validatedManifest.contentHash,
        startedAt,
        finishedAt,
        status: "skipped",
        insertedCount: 0,
        updatedCount: 0,
        deletedCount: 0,
        errorMessage: null,
      };
      await database.transaction("rw", database.syncRuns, database.settings, async () => {
        await database.syncRuns.put(run);
        await database.settings.put({ key: "lastSyncAt", value: finishedAt, updatedAt: finishedAt });
      });
      return { status: "skipped", manifest: validatedManifest, run };
    }

    const [noticesText, changesText, evaluationsText] = await Promise.all([
      fetchText(fetcher, joinDataUrl(baseUrl, "notices.json")),
      fetchText(fetcher, joinDataUrl(baseUrl, "changes.json")),
      fetchText(fetcher, joinDataUrl(baseUrl, "evaluations.json")),
    ]);
    const texts = { noticesText, changesText, evaluationsText };
    const actualHash = await computeDatasetHash(texts, options.crypto);
    if (actualHash !== validatedManifest.contentHash) throw new DatasetHashMismatchError();

    // Every byte is fetched and every record is validated before opening the
    // write transaction. No partially parsed dataset can reach IndexedDB.
    const dataset = parseAndValidateDatasetFiles(validatedManifest, texts);
    const evaluationByNoticeId = new Map(
      dataset.evaluations.map((evaluation) => [evaluation.noticeId, evaluation]),
    );
    const incomingRecords = dataset.notices.map((notice) =>
      toRemoteNoticeRecord(notice, evaluationByNoticeId.get(notice.id) ?? null),
    );

    let run: SyncRun | null = null;
    await database.transaction(
      "rw",
      [
        database.remoteNotices,
        database.remoteChanges,
        database.remoteEvaluations,
        database.syncRuns,
        database.settings,
      ],
      async () => {
        const [existingNotices, existingChanges, existingEvaluations] = await Promise.all([
          database.remoteNotices.toArray(),
          database.remoteChanges.toArray(),
          database.remoteEvaluations.toArray(),
        ]);
        const existingById = new Map(existingNotices.map((notice) => [notice.id, notice]));
        const incomingIds = new Set(incomingRecords.map((notice) => notice.id));
        const missingRecords: RemoteNoticeRecord[] = existingNotices
          .filter((notice) => !incomingIds.has(notice.id))
          .map((notice) => ({ ...notice, sourceMissing: true }));
        const missingIds = new Set(missingRecords.map((notice) => notice.id));

        const retainedEvaluations = existingEvaluations.filter(
          (evaluation) =>
            missingIds.has(evaluation.noticeId) &&
            !evaluationByNoticeId.has(evaluation.noticeId),
        );
        const incomingChangeIds = new Set(dataset.changes.map((change) => change.id));
        const retainedChanges = existingChanges.filter(
          (change) => missingIds.has(change.noticeId) && !incomingChangeIds.has(change.id),
        );

        const insertedCount = incomingRecords.filter(
          (notice) => !existingById.has(notice.id),
        ).length;
        const updatedCount =
          incomingRecords.filter((notice) => {
            const existing = existingById.get(notice.id);
            return (
              existing !== undefined &&
              (existing.contentHash !== notice.contentHash || existing.sourceMissing)
            );
          }).length + missingRecords.filter((notice) => !existingById.get(notice.id)?.sourceMissing).length;
        const finishedAt = now().toISOString();
        run = {
          id: runId,
          datasetVersion: validatedManifest.datasetVersion,
          contentHash: validatedManifest.contentHash,
          startedAt,
          finishedAt,
          status: "success",
          insertedCount,
          updatedCount,
          // These records remain readable with sourceMissing=true. The count
          // communicates how many disappeared from the source snapshot.
          deletedCount: missingRecords.length,
          errorMessage: null,
        };

        await database.remoteNotices.clear();
        await database.remoteChanges.clear();
        await database.remoteEvaluations.clear();
        await database.remoteNotices.bulkPut([...incomingRecords, ...missingRecords]);
        await database.remoteChanges.bulkPut([...dataset.changes, ...retainedChanges]);
        await database.remoteEvaluations.bulkPut([
          ...dataset.evaluations,
          ...retainedEvaluations,
        ]);
        await database.syncRuns.put(run);
        await database.settings.bulkPut([
          { key: "lastSyncDatasetVersion", value: validatedManifest.datasetVersion, updatedAt: finishedAt },
          { key: "lastSyncContentHash", value: validatedManifest.contentHash, updatedAt: finishedAt },
          { key: "lastSyncAt", value: finishedAt, updatedAt: finishedAt },
        ]);
      },
    );

    if (!run) throw new DatasetValidationError("Synchronization transaction did not finish.");
    await reapplyStoredCompanyProfile(database, now().toISOString());
    return { status: "success", manifest: validatedManifest, run };
  } catch (error) {
    const finishedAt = now().toISOString();
    const failedRun: SyncRun = {
      id: runId,
      datasetVersion: manifest?.datasetVersion ?? "unknown",
      contentHash: manifest?.contentHash ?? "",
      startedAt,
      finishedAt,
      status: "failed",
      insertedCount: 0,
      updatedCount: 0,
      deletedCount: 0,
      errorMessage: safeErrorMessage(error),
    };
    await recordFailedRun(database, failedRun);
    throw error;
  }
}

/**
 * Gives the UI its local snapshot immediately and a separate background task
 * it can observe without blocking first paint.
 */
export async function initializeLocalFirst(
  database: BrowserDatabase,
  options: DatasetSyncOptions = {},
): Promise<{
  localNotices: Awaited<ReturnType<typeof listDashboardNotices>>;
  synchronization: Promise<DatasetSyncResult>;
}> {
  const localNotices = await listDashboardNotices(database);
  return {
    localNotices,
    synchronization: synchronizeDataset(database, options),
  };
}
